import { v4 as uuidv4 } from 'uuid';
import Title from '../../../../components/forms/titles.component';
import Icon from '../../../../components/forms/icon.component';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BotsService } from '../../../../services/bots.service';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { STORAGE } from '../../../../constants';
import { AlertService } from '../../../../services/alert.service';

interface PreviewPanelProps {
  botId: string;
  onClose: () => void;
}
type ChatMessage =
  | { sendBy: 'me'; type: 'TEXT'; text: string }
  | { sendBy: string; type: 'TEXT'; text: string; node_type: string; node_id: string }
  | { sendBy: string; type: 'BUTTONS'; buttons: any; node_type: string; node_id: string }
  | { sendBy: string; type: 'LIST'; list: any; node_type: string; node_id: string }
  | { sendBy: string; type: 'IMAGE'; image: any; node_type: string; node_id: string };

const PreviewPanel: React.FC<PreviewPanelProps> = ({ botId, onClose }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [listMessages, setListMessages] = useState<ChatMessage[]>([]);
  const [ticketId, setTicketId] = useState(() => uuidv4());
  const lastNodeType = useRef<string | null>(null);
  const user = LocalStorageService.get(STORAGE.SESSION);

  const sendMessage = async (msgOverride?: string) => {
    const text = msgOverride ?? message;
    if (!text.trim()) return;

    setListMessages((prev) => prev.concat([{ sendBy: 'me', type: 'TEXT', text }]));
    setMessage('');

    try {
      const data = await BotsService.startFlow({
        bot: botId,
        ticket: ticketId,
        phone_number: '5215512345678',
        name: user.username,
        message: text,
        is_sandbox: true
      });

      const responses = data.messages.map((m: any) => {
        lastNodeType.current = data.type;
        return {
          sendBy: 'Bot',
          node_type: data.type,
          node_id: data.node_id,
          ...m
        } as ChatMessage;
      });

      // setListMessages((prev) => prev.concat(responses));
      setListMessages((ch) => [...ch, ...responses]);
    } catch (error: any) {
      console.error('Error:', error);
      AlertService.toastError(error.error || error);
      return;
    }
  };

  const renderText = (m: any) => {
    return (
      <div className="d-flex flex-column">
        <span className="fw-bolder">{m.sendBy}</span>
        <p className="mb-0">{m.text}</p>
      </div>
    );
  };

  const renderImage = (m: any) => {
    return (
      <div className="d-flex flex-column">
        <span className="fw-bolder">{m.sendBy}</span>
        <img src={m.image?.link} alt="preview" className="img-fluid rounded" />
        {m?.image?.caption && (
          <label className="form-label mt-1">{m.image.caption || m.image.es?.caption}</label>
        )}
      </div>
    );
  };

  const renderList = (m: any) => {
    return (
      <div className="d-flex flex-column">
        <span className="fw-bolder">{m.sendBy}</span>
        <ul className="list-unstyled mb-0">
          {m.list.es.map((item: any, i: number) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderButtons = (m: any) => {
    return (
      <div className="d-flex flex-column">
        <span className="fw-bolder">{m.sendBy}</span>
        <ul className="list-unstyled mb-0">
          {m.buttons.map((btn: any, i: number) => (
            <li key={i}>
              <button className="btn btn-primary" onClick={() => console.log('Clicked', btn)}>
                {btn.es}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // const renderVideo = (m: any) => {
  //   return (
  //     <div className="d-flex flex-column">
  //       <span className="fw-bolder">{m.sendBy}</span>
  //       <video controls className="rounded" controlsList="nodownload nofullscreen noremoteplayback">
  //         <source src={m.video?.link} type="video/mp4" />
  //         Your browser does not support the video tag.
  //       </video>
  //       {m?.video?.caption && (
  //         <label className="form-label mt-1">{m.video.caption || m.video.es?.caption}</label>
  //       )}
  //     </div>
  //   );
  // };

  useEffect(() => {
    setTicketId(uuidv4());
  }, []);

  return (
    <div className="shadow-sm preview-panel pb-4 pt-5 px-2">
      <div className="d-flex flex-column">
        <div className="flex-grow-1 p-6">
          <div className="d-flex align-items-center justify-content-between">
            <Title
              icon="ki-double-right-arrow"
              title="Previzualización de tu flujo"
              subtitle="Valida las respuestas de tu chatbot antes de liberarlo para tus clientes"
              type="info"
            />

            <div onClick={onClose}>
              <Icon
                icon="ki-cross"
                size="fs-3x"
                className="hover-icon position-absolute pt-4 pe-2 top-0 end-0 cursor-pointer"
                type="danger"
              />
            </div>
          </div>
          <div className="separator text-dark mb-7"></div>
          <div className="px-3 flex-grow-1 overflow-auto" style={{ maxHeight: 400 }}>
            {listMessages.map((m, i) => (
              <div className="d-flex mb-3 justify-content-end pe-2" key={i}>
                {m.sendBy !== 'me' && (
                  <div className="col-2 text-start align-self-end">
                    <span className="avatar avatar-lg avatar-circle me-3 border">
                      <span className="avatar-initials">🤖</span>
                    </span>
                  </div>
                )}
                <div
                  className={`chat-message-content flex-grow-1 ${m.sendBy !== 'me' ? 'ms-3' : 'justify-content-end'}`}
                >
                  <div
                    className={`border p-2 px-4 bg-light  ${m.sendBy !== 'me' ? 'message-rounded-receive' : 'message-rounded-send'}`}
                  >
                    {m.type === 'TEXT' && renderText(m)}
                    {m.type === 'IMAGE' && renderImage(m)}
                    {m.type === 'LIST' && renderList(m)}
                    {m.type === 'BUTTONS' && renderButtons(m)}
                  </div>
                </div>
                {m.sendBy === 'me' && (
                  <div className="col-2 text-end align-self-end">
                    <span className="avatar avatar-lg avatar-circle ms-3 border">
                      <span className="avatar-initials">👨‍💻</span>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="border-top p-6 position-absolute bottom-0 end-0 w-100">
            <div className="d-flex">
              <input
                type="text"
                className="form-control me-2"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={t('Escribe tu mensaje y presiona Enter')}
              />
              <button className="btn btn-primary" onClick={() => sendMessage()}>
                <i className="bi-send-fill"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewPanel;
